import ConfirmAccountSuccess from 'pages/auth/ConfirmAccountSuccess';
import ConfirmAccount from 'pages/auth/ConfirmAccount';
import RequestResetPassword from 'pages/auth/RequestResetPassword';
import ResetPasswordSuccess from 'pages/auth/ResetPasswordSuccess';
import ResetPassword from 'pages/auth/ResetPassword';
import { useEffect } from 'react'; // Import useEffect for programmatic navigation
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useParams, // Import useParams to extract URL parameters
  useNavigate, // Import useNavigate for programmatic navigation
} from 'react-router-dom';
import RouteGuard from 'components/shared/route-guard';
import PageNotFound from 'pages/PageNotFound';
import ConfirmEmail from 'pages/auth/ConfirmEmail';
import Login from 'pages/auth/Login';
import LoginWithToken from 'pages/auth/LoginWithToken';
import SignUp from 'pages/auth/SignUp';
import SignupWithProperty from 'pages/auth/property/SignupWithProperty';
import Welcome from 'pages/auth/Welcome';
import ApplyForClaim from 'pages/claim/ApplyForClaim';
import ClaimDetails from 'pages/dashboard/claims/ClaimDetails';
import Claims from 'pages/dashboard/claims/Claims';
import DashboardHome from 'pages/dashboard/home/DashboardHome';
import PlanDetails from 'pages/dashboard/plans/PlanDetails';
import Plans from 'pages/dashboard/plans/Plans';
import Settings from 'pages/dashboard/settings/Settings';
import NavigationTracker from 'common/utils/user-tracker/navigation';
import ReviewClaim from 'pages/claim/ReviewClaim';
import EmailLoginExchange from 'pages/auth/EmailLoginExchange';
import EmailLoginRequest from 'pages/auth/EmailLoginRequest';
import AttachPlan from 'pages/auth/AttachPlan';
import SignupWithOwner from 'pages/auth/property/SignupWithOwner';
import PurchasePlan from 'pages/plan/purchase';
import PlanQuote from 'pages/plan/quote';
import PropertyLanding from 'pages/auth/property/PropertyLanding';
import PlanActivate from 'pages/plan/activate';

// A generic component to handle redirects with URL parameters
const RedirectWithParams = ({ toPath }) => {
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let finalPath = toPath;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        finalPath = finalPath.replace(`:${key}`, params[key]);
      }
    }
    navigate(finalPath, { replace: true });
  }, [params, navigate, toPath]);
  return null; // This component doesn't render anything, it just redirects
};

const AppRouter = () => {  
  return (
    <Router>
      <NavigationTracker />
      <Routes>
        {/* NOT FOUND/ INVALID ROUTE */}
        <Route path='*' element={<PageNotFound />} />

        {/* Temporary routes that will probably get moved */}
        <Route path='/dashboard/plans/:planId/pdf' element={
          <Navigate to='/dashboard/home' replace />
        } />        

        {/* This is how an admin will impersonate a user */}
        <Route path='/use-login-token' element={<LoginWithToken />} />

        {/* AUTH */}
        <Route path='/login-with-password' element={<Login />} />
        <Route path='/login' element={<EmailLoginRequest />} />
        <Route
          path='/email-login-request'
          element={<Navigate to='/login' replace />}
        />
        <Route path='/email-login-exchange' element={<EmailLoginExchange />} />
        <Route path='/attach-plan' element={<AttachPlan />} />

        <Route
          path='/reset-password-success'
          element={<ResetPasswordSuccess />}
        />
        <Route
          path='/reset-password-request'
          element={<RequestResetPassword />}
        />
        <Route path='reset-email' element={<ResetPassword />} />
        <Route path='/signup' element={<SignUp />} />
        <Route
          path='/properties/:propertyID/signup'
          element={<SignupWithProperty />}
        />
        <Route
          path='/properties/:propertyID'
          element={<PropertyLanding />}
        />
        <Route
          path='/owners/:ownerID/signup'
          element={<SignupWithOwner />}
        />
        <Route
          path='/owners/:ownerID'
          element={<RedirectWithParams toPath='/owners/:ownerID/signup' />}
        />

        {/* Make sure old property and owner signup routes redirect correctly */}
        {/* Updated to use RedirectWithParams for dynamic parameter handling */}
        <Route
          path='/signup/properties/:propertyID'
          element={<RedirectWithParams toPath='/properties/:propertyID' />}
        />
        <Route
          path='/signup/owners/:ownerID'
          element={<RedirectWithParams toPath='/owners/:ownerID' />}
        />

        <Route
          path='/account-created-success'
          element={<ConfirmAccountSuccess />}
        />
        <Route path='/confirm-account' element={<ConfirmAccount />} />
        <Route path='/confirm-email' element={<ConfirmEmail />} />

        {/* ROUTE GUARD */}
        <Route element={<RouteGuard />}>          

          {/* DASHBOARD WELCOME */}
          <Route path='/welcome' element={<Welcome />} />

          {/* DASHBOARD HOME */}
          {/* Redirect to the preferred route */}
          <Route path='/' element={<Navigate to='/dashboard/home' replace />} />
          <Route
            path='/dashboard'
            element={<Navigate to='/dashboard/home' replace />}
          />
          {/* Preferred route */}
          <Route path='/dashboard/home' element={<DashboardHome />} />

          {/* DASHBOARD CLAIMS */}
          <Route
            path='/dashboard/claims/submit/:claimId'
            element={<ApplyForClaim />}
          />
          <Route path='/dashboard/claims/submit' element={<ApplyForClaim />} />
          <Route path='/dashboard/claims' element={<Claims />} />
          <Route path='/dashboard/claims/:claimId' element={<ClaimDetails />} />
          <Route
            path='/dashboard/claims/:claimId/review'
            element={<ReviewClaim />}
          />
          {/* Redirect to claims for old routes */}
          <Route path='/claims' element={<Navigate to='/dashboard/claims' replace />} />
          <Route
            path='/claims/submit/:claimId'
            element={<Navigate to='/dashboard/claims/' replace />}
          />
          <Route
            path='/claims/submit'
            element={<Navigate to='/dashboard/claims/submit' replace />}
          />
          <Route
            path='/claims/:claimId'
            element={<Navigate to='/dashboard/claims/' replace />}
          />
          <Route
            path='/claims/:claimId/review'
            element={<Navigate to='/dashboard/claims/' replace />}
          />

          {/* DASHBOARD PLANS */}
          <Route path='/dashboard/plans/quote' element={<PlanQuote />} />
          <Route path='/dashboard/plans/quote/:planId' element={<PlanQuote />} />
          <Route path='/dashboard/plans/purchase' element={<PurchasePlan />} />
          <Route path='/dashboard/plans/purchase/:planId' element={<PurchasePlan />} />
          <Route path='/dashboard/plans/activate/:planId' element={<PlanActivate />} />
          <Route path='/dashboard/plans' element={<Plans />} />
          <Route path='/dashboard/plans/:planId' element={<PlanDetails />} />

          {/* Old routes */}
          <Route path='/dashboard/plans/apply' element={<Navigate to='/dashboard/plans/quote' replace />} />

          {/* DASHBOARD SETTINGS */}
          <Route path='dashboard/settings' element={<Settings />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default AppRouter;
